<template>
  <div
    class="bg-image h-100 position-relative overflow-hidden"
    :style="{
      backgroundImage: `url(${backgroundSrc})`,
      backgroundColor: backgroundColor
    }"
    :class="`${backgroundClass}`"
  >
    <div class="container mt-4 text-start overflow-auto">
      <div class="row">
        <div
          :class="{
            col: useInstructions,
            'col-12': !useInstructions
          }"
        >
          <div class="card bg-white bg-opacity-90 mb-3">
            <div class="card-body py-2 px-3">
              <h1 class="mb-0 text-start">{{ appTitle }}</h1>
            </div>
          </div>
          <div class="card bg-white bg-opacity-90 mb-3">
            <div class="card-body p-4">
              <!-- Overview Section -->
              <div class="mb-4">
                <h2 class="h4 mb-3">Overview</h2>
                <p>{{ contentData.overview }}</p>
              </div>

              <!-- Tabbed Content -->
              <div class="mb-4">
                <ul class="nav nav-tabs">
                  <li
                    v-for="tab in contentData.tabs"
                    :key="tab.id"
                    class="nav-item"
                  >
                    <button
                      :class="['nav-link', { active: activeTab === tab.id }]"
                      :id="`${tab.id}-tab`"
                      type="button"
                      role="tab"
                      :aria-controls="tab.id"
                      :aria-selected="activeTab === tab.id"
                      @click="activeTab = tab.id"
                    >
                      {{ tab.title }}
                    </button>
                  </li>
                </ul>

                <div class="tab-content mt-3">
                  <div
                    v-for="tab in contentData.tabs"
                    :key="tab.id"
                    :class="[
                      'tab-pane',
                      'fade',
                      { 'show active': activeTab === tab.id }
                    ]"
                    :id="tab.id"
                    role="tabpanel"
                    :aria-labelledby="`${tab.id}-tab`"
                  >
                    <!-- Learning Objectives Tab -->
                    <div v-if="tab.id === 'learning-objectives'">
                      <h3 class="h5 mb-2">{{ tab.content.heading }}</h3>
                      <p>{{ tab.content.text }}</p>
                      <ul>
                        <li v-for="(item, idx) in tab.content.list" :key="idx">
                          {{ item }}
                        </li>
                      </ul>
                    </div>

                    <!-- Instructions Tab -->
                    <div v-else-if="tab.id === 'instructions'">
                      <h3 class="h5 mb-2">{{ tab.content.heading }}</h3>
                      <p>{{ tab.content.text }}</p>
                      <p class="fst-italic mt-2">{{ tab.content.note }}</p>
                    </div>

                    <!-- Purposes of Tillage Tab -->
                    <div v-else-if="tab.id === 'purposes-of-tillage'">
                      <h3 class="h5 mb-3">{{ tab.content.heading }}</h3>
                      <div
                        v-for="(section, idx) in tab.content.sections"
                        :key="idx"
                        class="mb-3"
                      >
                        <h4 class="h6 fw-bold">{{ section.subheading }}</h4>
                        <p>{{ section.text }}</p>
                      </div>
                    </div>

                    <!-- Soil Background Tab -->
                    <div v-else-if="tab.id === 'soil-background'">
                      <h3 class="h5 mb-2">{{ tab.content.heading }}</h3>
                      <p>{{ tab.content.intro }}</p>

                      <!-- Soil Texture Triangle Image -->
                      <figure
                        v-if="tab.content.images && tab.content.images[0]"
                        class="figure text-center d-block my-3"
                      >
                        <img
                          :src="getImageSrc(tab.content.images[0].src)"
                          :alt="tab.content.images[0].alt"
                          class="figure-img img-fluid rounded"
                          style="max-width: 400px"
                        />
                        <figcaption class="figure-caption">
                          {{ tab.content.images[0].caption }}
                          <span v-if="tab.content.images[0].attribution">
                            ({{ tab.content.images[0].attribution.license }})
                          </span>
                        </figcaption>
                      </figure>

                      <p class="fst-italic">{{ tab.content.note }}</p>

                      <h4 class="h6 fw-bold mt-3 mb-2">
                        {{ tab.content.characteristics.title }}
                      </h4>
                      <dl class="row">
                        <template
                          v-for="(item, idx) in tab.content.characteristics
                            .items"
                          :key="idx"
                        >
                          <dt class="col-sm-4">{{ item.label }}</dt>
                          <dd class="col-sm-8">{{ item.value }}</dd>
                        </template>
                      </dl>

                      <h5 class="fw-bold mt-3 mb-2">Structure</h5>
                      <ul class="list-unstyled">
                        <li
                          v-for="(struct, idx) in tab.content.characteristics
                            .structure"
                          :key="idx"
                          class="mb-2"
                        >
                          <strong>{{ struct.horizon }}</strong> ({{
                            struct.depth
                          }}):
                          {{ struct.description }}
                        </li>
                      </ul>

                      <!-- Soil Profile Images -->
                      <div
                        v-if="
                          tab.content.images && tab.content.images.length > 1
                        "
                        class="row mt-4"
                      >
                        <div
                          v-for="img in tab.content.images.slice(1)"
                          :key="img.id"
                          class="col-md-6 mb-3"
                        >
                          <figure v-if="!img.placeholder" class="figure">
                            <img
                              :src="getImageSrc(img.src)"
                              :alt="img.alt"
                              class="figure-img img-fluid rounded"
                            />
                            <figcaption class="figure-caption">
                              {{ img.caption }}
                            </figcaption>
                          </figure>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Activity page links -->
              <div class="d-flex justify-content-end gap-3">
                <routerLink
                  class="btn btn-lg btn-primary"
                  :to="{ name: 'TillageImplements' }"
                >
                  Tillage Implements
                </routerLink>
                <routerLink
                  class="btn btn-lg btn-primary"
                  :to="{ name: 'Activity' }"
                >
                  Tillage Simulation
                </routerLink>
              </div>
            </div>
          </div>
          <div v-if="useControls" class="card bg-white bg-opacity-90">
            <div class="card-body p-4">
              <UserControls />
            </div>
          </div>
        </div>
        <div v-if="useInstructions" class="col">
          <div class="card bg-white bg-opacity-90">
            <div class="card-body p-4">
              <UserInstructions />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserInstructions from "@/components/ui/UserInstructions.vue";
import UserControls from "@/components/ui/UserControls.vue";
import config from "@/data/config.json";
import contentData from "@/data/overviewText.json";

// Import overview images directly
import soilTextureTriangle from "@/assets/overview/soil-texture-triangle.jpg";
import nekiaSoilProfile from "@/assets/overview/nekia-soil-profile.jpg";

const imageMap = {
  "overview/soil-texture-triangle.jpg": soilTextureTriangle,
  "overview/nekia-soil-profile.jpg": nekiaSoilProfile
};

export default {
  name: "LandingView",
  components: { UserInstructions, UserControls },
  data() {
    return {
      appTitle: config.meta.title,
      // From within the @/assets folder
      // Be aware of file size: Max Size 512kb
      backgroundImg: config.landingView.backgroundImg,
      backgroundAlt: config.landingView.backgroundAlt,
      backgroundColor: config.landingView.backgroundColor,
      backgroundClass: config.landingView.backgroundClass,
      callToActionText: config.landingView.callToActionText,
      callToActionTextDestination:
        config.landingView.callToActionTextDestination,
      useInstructions: config.landingView.useInstructions,
      useControls: config.landingView.useControls,
      contentData: contentData,
      activeTab: "learning-objectives"
    };
  },
  computed: {
    backgroundSrc() {
      if (!this.useBackgroundImg) {
        return;
      }

      return new URL(`../assets/${this.backgroundImg}`, import.meta.url).href;
    },
    useBackgroundImg() {
      return this.backgroundImg !== "";
    }
  },
  methods: {
    getImageSrc(src) {
      return imageMap[src] || src;
    }
  }
};
</script>
<style scoped>
.bg-image {
  background-size: contain;
  background-position: center bottom;
  background-repeat: no-repeat;
}
.bg-opacity-90 {
  --bs-bg-opacity: 0.9;
}

/* Enhanced tab styling */
.nav-tabs {
  border-bottom: 3px solid #6c757d;
}

.nav-tabs .nav-link {
  border: 2px solid #6c757d;
  border-bottom: none;
  color: #495057;
  font-weight: 500;
  padding: 0.75rem 1.25rem;
  transition: all 0.2s ease-in-out;
}

.nav-tabs .nav-link:hover {
  border-color: #495057 #495057 transparent;
  color: #212529;
  background-color: #e9ecef;
}

.nav-tabs .nav-link.active {
  color: #212529;
  font-weight: 600;
  background-color: #fff;
  border-color: #495057 #495057 transparent;
  border-top-width: 4px;
}
</style>
